// ─────────────────────────────────────────────────────────────────────────
// buildReleaseComparison(baseReportData, compareReportData, options)
//
// SOLE RESPONSIBILITY: produce a node-by-node diff between two already-built
// Release trees. This function NEVER queries Supabase, NEVER calls
// buildCostControlDataFromRelease() itself (the caller does that, once per
// Release, and hands both results in here), and NEVER knows anything about
// Variance Notes — note loading/saving lives entirely in
// ReleaseComparisonView.jsx, per explicit agreement. Node "changed" status
// here is a pure display/filter classification; it has no bearing on
// whether a note can be attached to a node — every node, regardless of
// status, is a valid note target.
//
// MATCHING KEY (explicitly agreed, and a known constraint of the current
// build contract): buildCostControlDataFromRelease() adapts every node's
// `id` from forecast_history.row_key, which IS the node's `code` — there is
// no underlying cost_codes/resource/sub_code UUID preserved in a Release
// snapshot. Therefore this function matches nodes between the two trees by
// `code` (case-sensitive as stored), not by `id`/UUID. This is a known,
// accepted limitation of the current Release contract, not an oversight —
// renaming a code between two Releases will show as one item "removed" and
// a different item "new" rather than a single "changed" item. This applies
// identically whether either side is a Release tree or (in the future) a
// Live tree, since Live's `code` values are the same stable text used here.
//
// CALCULATION SCOPE (explicitly limited to subtraction — no new business
// logic): the only arithmetic this file performs is
//   variance = compareValue - baseValue
// applied to whichever pre-computed field the caller is comparing (eac by
// default). This is the same class of operation as the existing
// `varianceCompletion = bac - eac` already computed elsewhere in the
// codebase — a simple difference of two numbers the build layer already
// produced, not a new financial formula.
//
// SCOPE FILTER: options.scope is either 'project' (no filtering) or
// { costCodeCode } — a plain `code` string (NOT an id/UUID, per the same
// constraint above) identifying which top-level Cost Code's subtree to
// limit the comparison to.
//
// OUTPUT SHAPE: a tree mirroring the Direct/Indirect Cost Code → Resource →
// Sub Code shape both buildCumulativeData() and
// buildCostControlDataFromRelease() already produce, with each node
// carrying:
//   { code, description, level: 'costcode'|'resource'|'subcode',
//     baseValue, compareValue, variance, status: 'unchanged'|'changed'|'new'|'removed',
//     children: [...], resourceRows: [...] (Cost Code level only) }

const COMPARE_FIELD = 'eac' // the field variance is computed on, per node

function emptyValue() { return 0 }

// Index a flat list of Cost Code root nodes (each with resourceRows/children,
// exactly as buildCostControlDataFromRelease()/buildCumulativeData() already
// produce) into a Map keyed by `code`, at every level, for O(1) lookups
// during the diff pass. Does not mutate the input.
function indexTreeByCode(rootNodes) {
  const map = new Map() // code -> node
  for (const parent of rootNodes) {
    map.set(parent.code, parent)
    for (const res of (parent.resourceRows || [])) {
      map.set(res.code, res)
    }
    for (const child of (parent.children || [])) {
      map.set(child.code, child)
    }
  }
  return map
}

function diffNode(baseNode, compareNode, level) {
  const baseValue = baseNode ? (baseNode[COMPARE_FIELD] || 0) : emptyValue()
  const compareValue = compareNode ? (compareNode[COMPARE_FIELD] || 0) : emptyValue()
  const variance = compareValue - baseValue

  let status
  if (!baseNode && compareNode) status = 'new'
  else if (baseNode && !compareNode) status = 'removed'
  else if (variance !== 0) status = 'changed'
  else status = 'unchanged'

  const source = compareNode || baseNode
  return {
    code: source.code,
    description: source.description,
    level,
    baseValue,
    compareValue,
    variance,
    status,
  }
}

// Diffs one Cost Code's full subtree (Resource rows + flat Sub Code rows),
// matching by `code` on both sides. Either side may be missing entirely
// (a whole-subtree 'new' or 'removed' case) — handled by passing an empty
// synthetic parent for the missing side, so every node under it still
// surfaces as new/removed individually.
function diffCostCodeSubtree(baseParent, compareParent) {
  const source = compareParent || baseParent
  const parentDiff = diffNode(baseParent, compareParent, 'costcode')

  // Resource-level rows
  const baseResources = (baseParent?.resourceRows) || []
  const compareResources = (compareParent?.resourceRows) || []
  const resourceCodes = new Set([...baseResources.map(r => r.code), ...compareResources.map(r => r.code)])
  const baseResByCode = new Map(baseResources.map(r => [r.code, r]))
  const compareResByCode = new Map(compareResources.map(r => [r.code, r]))
  const resourceRows = [...resourceCodes].map(code =>
    diffNode(baseResByCode.get(code) || null, compareResByCode.get(code) || null, 'resource')
  )

  // Flat Sub Code rows (both under-a-resource and orphan, exactly matching
  // the existing flat children[] contract)
  const baseChildren = (baseParent?.children) || []
  const compareChildren = (compareParent?.children) || []
  const childCodes = new Set([...baseChildren.map(c => c.code), ...compareChildren.map(c => c.code)])
  const baseChildByCode = new Map(baseChildren.map(c => [c.code, c]))
  const compareChildByCode = new Map(compareChildren.map(c => [c.code, c]))
  const children = [...childCodes].map(code =>
    diffNode(baseChildByCode.get(code) || null, compareChildByCode.get(code) || null, 'subcode')
  )

  return { ...parentDiff, resourceRows, children }
}

// Diffs a full Direct or Indirect root list (array of Cost Code nodes).
function diffRootList(baseRoots, compareRoots) {
  const baseByCode = new Map(baseRoots.map(p => [p.code, p]))
  const compareByCode = new Map(compareRoots.map(p => [p.code, p]))
  const allCodes = new Set([...baseByCode.keys(), ...compareByCode.keys()])
  return [...allCodes].map(code =>
    diffCostCodeSubtree(baseByCode.get(code) || null, compareByCode.get(code) || null)
  )
}

// Recursively keeps only nodes whose own status !== 'unchanged', OR that
// have at least one descendant that changed — preserving the parent chain
// for context (per explicit requirement: "if a Sub Code changed, display
// its parent Resource and Cost Code"). Does not alter baseValue/compareValue/
// variance/status on any kept node — filtering only, no recalculation.
function filterChangedOnly(costCodeDiffs) {
  function filterChildren(nodes) {
    return nodes.filter(n => n.status !== 'unchanged')
  }
  function resourceHasChange(res) {
    return res.status !== 'unchanged'
  }
  return costCodeDiffs
    .map(cc => {
      const filteredChildren = filterChildren(cc.children || [])
      const filteredResources = (cc.resourceRows || []).filter(resourceHasChange)
      // A Resource with no own change might still need to show because a
      // Sub Code under it changed — but per the flat children[] contract
      // (Sub Codes don't nest under resourceRows, they carry parent_id
      // separately), the existing screen re-groups them at render time.
      // For the changed-only filter, a Resource is kept if it changed OR
      // if any surviving child's parent_id references it.
      const survivingChildParentIds = new Set(filteredChildren.map(c => c.parent_id).filter(Boolean))
      const resourcesToKeep = (cc.resourceRows || []).filter(res =>
        res.status !== 'unchanged' || survivingChildParentIds.has(res.code)
      )
      const hasAnyChange = cc.status !== 'unchanged' || resourcesToKeep.length > 0 || filteredChildren.length > 0
      if (!hasAnyChange) return null
      return { ...cc, resourceRows: resourcesToKeep, children: filteredChildren }
    })
    .filter(Boolean)
}

// Limits a root list to a single Cost Code's subtree by `code` (per the
// explicit scope-by-code decision, matching the Release contract's own key).
function applyScope(costCodeDiffs, scope) {
  if (!scope || scope === 'project') return costCodeDiffs
  if (scope.costCodeCode) {
    return costCodeDiffs.filter(cc => cc.code === scope.costCodeCode)
  }
  return costCodeDiffs
}

export function buildReleaseComparison(baseReportData, compareReportData, options = {}) {
  const { mode = 'full', scope = 'project', baseRelease = null, compareRelease = null } = options

  const baseDirect = baseReportData?.directRows || []
  const compareDirect = compareReportData?.directRows || []
  const baseIndirect = baseReportData?.indirectRows || []
  const compareIndirect = compareReportData?.indirectRows || []

  let directDiffs = diffRootList(baseDirect, compareDirect)
  let indirectDiffs = diffRootList(baseIndirect, compareIndirect)

  directDiffs = applyScope(directDiffs, scope)
  indirectDiffs = applyScope(indirectDiffs, scope)

  if (mode === 'changed') {
    directDiffs = filterChangedOnly(directDiffs)
    indirectDiffs = filterChangedOnly(indirectDiffs)
  }

  // Grand-total-level variance — a pure difference of the two Release
  // trees' own already-computed grandTotals.eac, not a new calculation.
  const baseGrandEac = baseReportData?.grandTotals?.eac || 0
  const compareGrandEac = compareReportData?.grandTotals?.eac || 0
  const totalVariance = compareGrandEac - baseGrandEac
  const totalVariancePct = baseGrandEac > 0 ? (totalVariance / baseGrandEac) * 100 : 0

  // Flatten every node across both trees (post-scope, pre-mode-filter is
  // irrelevant here — summary counts should reflect the FULL comparison
  // regardless of the 'changed only' display filter, so callers get an
  // honest count even while viewing a filtered tree) for summary counts.
  const fullDirectDiffs = applyScope(diffRootList(baseDirect, compareDirect), scope)
  const fullIndirectDiffs = applyScope(diffRootList(baseIndirect, compareIndirect), scope)

  function flattenAll(costCodeDiffs) {
    const flat = []
    for (const cc of costCodeDiffs) {
      flat.push(cc)
      flat.push(...(cc.resourceRows || []))
      flat.push(...(cc.children || []))
    }
    return flat
  }
  const allNodes = [...flattenAll(fullDirectDiffs), ...flattenAll(fullIndirectDiffs)]

  const changedCount = allNodes.filter(n => n.status === 'changed').length
  const newCount = allNodes.filter(n => n.status === 'new').length
  const removedCount = allNodes.filter(n => n.status === 'removed').length
  const topVariances = [...allNodes]
    .filter(n => n.status !== 'unchanged')
    .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
    .slice(0, 10)

  // revisionMeta — plain display metadata only, read straight off the two
  // Release rows the caller already has (project_forecast_release_summary
  // fields), no derivation beyond string/number selection. Lets the screen
  // show exactly which Direct/Indirect Revision is being compared against
  // which, and flag when a side's Revision is identical in both Releases
  // (e.g. Indirect unchanged between Release #1 and #2) — this is display
  // truth, not a computed variance.
  const revisionMeta = {
    direct: {
      baseRevisionLabel: baseRelease?.direct_snapshot_id ? `Rev.${baseRelease.direct_revision_number}` : 'None',
      compareRevisionLabel: compareRelease?.direct_snapshot_id ? `Rev.${compareRelease.direct_revision_number}` : 'None',
      sameRevision: !!baseRelease?.direct_snapshot_id && baseRelease.direct_snapshot_id === compareRelease?.direct_snapshot_id,
    },
    indirect: {
      baseRevisionLabel: baseRelease?.indirect_snapshot_id ? `Rev.${baseRelease.indirect_revision_number}` : 'None',
      compareRevisionLabel: compareRelease?.indirect_snapshot_id ? `Rev.${compareRelease.indirect_revision_number}` : 'None',
      sameRevision: !!baseRelease?.indirect_snapshot_id && baseRelease.indirect_snapshot_id === compareRelease?.indirect_snapshot_id,
    },
  }

  return {
    directDiffs,
    indirectDiffs,
    revisionMeta,
    summary: {
      totalVariance,
      totalVariancePct,
      changedCount,
      newCount,
      removedCount,
      topVariances,
    },
  }
}
